/**
 * Personalizar: um modal de dois painéis, como o da Meta Geral. À esquerda os ajustes, à direita a
 * tela sendo desenhada com o DADO REAL do período — não um exemplo inventado.
 *
 * Nas telas de lista o "Exibir como" é a própria navegação: Tabela e Cards são os dois desenhos da
 * mesma lista, e o painel mostra os ajustes do desenho escolhido. Na Geral as abas são os blocos,
 * os indicadores e o gráfico, e a pré-visualização desenha o bloco da aba aberta.
 *
 * A pré-visualização é viva: o cabeçalho da tabela ordena, o seletor de modelo do gráfico troca o
 * gráfico, e tudo o que se mexe ali já fica salvo — é a tela, não uma figura dela.
 *
 * A ordem se muda arrastando (mouse) ou pelos botões de subir e descer (teclado): arrastar sozinho
 * deixaria a reordenação fora do alcance de quem não usa mouse.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Lock } from 'lucide-react';
import { Check, CloseBtn, Segmented, Toggle } from '@/components/v2/ui';
import { c, v } from '@/components/v2/theme';
import { BLOCK_HUE, BLOCK_LABEL, BLOCKS, COLUMN_SETS, METRICS, M } from '../lib/metrics';
import {
    BLOCK_META, CARD_BLOCKS, CARD_BLOCK_META, CARD_SIZES, CHART_MODELS, DISPLAYS, KPI_CHOICES, KPI_STYLES,
    SCREEN_BLOCK_META,
    type CardBlock, type CardSize, type ChartModel, type Display, type KpiStyle, type OverviewBlock,
    type ScreenBlock, type SortPref,
} from '../lib/prefs';
import { SCREEN, type ScreenKey } from '../lib/screens';
import { useEmail } from '../state/store';
import { DataBlocksPreview } from './DataBlocks';
import { DataCards } from './DataView';
import { MetricCards } from './MetricCards';
import { BlocksPreview, chartModel, regModels } from './ScreenBlocks';
import { MetricTable, metricCols, sortRows } from './MetricTable';

type Tab = 'blocks' | 'kpis' | 'chart';
/** Nas telas de lista a última aba é a própria lista, que tem os dois desenhos. */
type ListTab = Tab | 'list';

const OVERVIEW_TABS: { k: Tab; l: string }[] = [
    { k: 'blocks', l: 'Blocos' }, { k: 'kpis', l: 'Indicadores' }, { k: 'chart', l: 'Gráfico' },
];
const LIST_TABS: { k: ListTab; l: string }[] = [...OVERVIEW_TABS, { k: 'list', l: 'A lista' }];

/** A faixa de abas do painel da esquerda — a mesma nas duas formas de tela. */
function Tabs<T extends string>({ opts, value, onChange }: { opts: { k: T; l: string }[]; value: T; onChange: (k: T) => void }) {
    return (
        <div style={{ display: 'flex', gap: 24, borderBottom: `1px solid ${v('border')}` }}>
            {opts.map((t) => {
                const on = t.k === value;
                return (
                    <button key={t.k} type="button" onClick={() => onChange(t.k)} aria-current={on || undefined}
                        style={{
                            height: 40, padding: '0 2px', border: 0, background: 'transparent', cursor: 'pointer',
                            font: 'inherit', fontSize: 14, color: on ? v('text') : v('muted'),
                            fontWeight: on ? 600 : 400, borderBottom: `2px solid ${on ? v('text') : 'transparent'}`,
                        }}>
                        {t.l}
                    </button>
                );
            })}
        </div>
    );
}

/** Quantas linhas a pré-visualização desenha: o bastante para ver a grade, não a lista inteira. */
const PREVIEW_CARDS = 4;
const PREVIEW_ROWS = 6;

/** Abaixo disto o modal fica só com os ajustes: a pré-visualização não caberia ao lado. */
function useWide(px: number) {
    const q = `(min-width: ${px}px)`;
    const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
    useEffect(() => {
        const m = window.matchMedia(q);
        const sync = () => setWide(m.matches);
        sync();
        m.addEventListener('change', sync);
        return () => m.removeEventListener('change', sync);
    }, [q]);
    return wide;
}

export function Customize({ screenKey }: { screenKey: ScreenKey }) {
    const { drawer, setDrawer, prefs, setScreenPrefs, setOverviewPrefs, resetOverview, resetScreen, blocks } = useEmail();
    const isOverview = screenKey === 'overview';
    const open = drawer != null;
    const wide = useWide(1180);
    const p = prefs.screens[screenKey];
    const tab: Tab = OVERVIEW_TABS.some((t) => t.k === drawer) ? (drawer as Tab) : 'blocks';
    // numa tela de lista, 'cols' e 'cards' são a mesma aba: a lista, nos seus dois desenhos
    const listTab: ListTab = OVERVIEW_TABS.some((t) => t.k === drawer) ? (drawer as Tab) : 'list';
    const metricList = SCREEN[screenKey].metricList;
    /**
     * Uma tela sem as 32 métricas pode ter blocos próprios (Cliques) ou não ter nenhum (Leads, que
     * já traz os gráficos dela). Quem diz é a tela que se registrou — não um sinalizador fixo: a
     * mesma tela tem visão com bloco e visão sem. Sem isso, o modal mostraria abas que não mexem
     * em nada.
     */
    const dataReg = blocks.current?.kind === 'data' ? blocks.current : null;
    /**
     * As abas de uma tela de números próprios: só as dos blocos que a visão tem. A aba fica presa
     * entre telas (o modal guarda qual estava aberta), então uma que não existe aqui cai em Blocos
     * — senão o painel abriria vazio.
     */
    const dataTabs = dataReg
        ? LIST_TABS.filter((t) => t.k === 'list' || t.k === 'blocks' || dataReg.blocks.includes(t.k as ScreenBlock))
        : [];
    const dataTab: ListTab = dataTabs.some((t) => t.k === listTab) ? listTab : 'blocks';
    /** Numa tela com blocos, as abas de bloco desenham o BLOCO no painel da direita; a da lista, a lista. */
    const shown = metricList ? listTab : dataTab;
    const blockTab: ScreenBlock | 'all' | null = isOverview || (!metricList && !dataReg)
        ? null
        : shown === 'kpis' ? 'kpis' : shown === 'chart' ? 'chart' : shown === 'blocks' ? 'all' : null;

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setDrawer(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, setDrawer]);

    if (!open) return null;
    const close = () => setDrawer(null);

    return (
        <div className="rv" onClick={close}
            style={{
                position: 'fixed', inset: 0, zIndex: 70, padding: 24, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'rgb(var(--rv-overlay) / 0.74)', backdropFilter: 'blur(6px)',
            }}>
            <div role="dialog" aria-modal aria-label="Personalizar a tela" onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: wide ? 1360 : 560, height: 'min(880px, 100%)',
                    background: v('modal'), border: `1px solid ${v('border-2')}`, borderRadius: 20,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}>
                <div style={{ padding: '18px 22px', borderBottom: `1px solid ${v('border')}`, display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 600 }}>Personalizar visualização</div>
                        <div style={{ fontSize: 12.5, color: v('muted'), marginTop: 3 }}>
                            {SCREEN[screenKey].l} · a pré-visualização usa o dado real do período
                        </div>
                    </div>
                    <CloseBtn onClick={close} size={32} />
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                    <div style={{
                        width: wide ? 420 : '100%', flexShrink: 0, minHeight: 0,
                        borderRight: wide ? `1px solid ${v('border')}` : 0, display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {isOverview ? (
                                <>
                                    <Tabs opts={OVERVIEW_TABS} value={tab} onChange={setDrawer} />
                                    {tab === 'blocks' && (
                                        <BlocksTab
                                            blocks={prefs.overview.blocks} hidden={prefs.overview.hidden} meta={BLOCK_META}
                                            onChange={setOverviewPrefs}
                                            hint="Arraste para mudar a ordem dos blocos na tela."
                                        />
                                    )}
                                    {tab === 'kpis' && (
                                        <KpisTab kpis={prefs.overview.kpis} kpiStyle={prefs.overview.kpiStyle} onChange={setOverviewPrefs} />
                                    )}
                                    {tab === 'chart' && <ChartTab />}
                                </>
                            ) : metricList ? (
                                <>
                                    <Tabs opts={LIST_TABS} value={listTab}
                                        onChange={(k) => setDrawer(k === 'list' ? (p.display === 'cards' ? 'cards' : 'cols') : k)} />
                                    {listTab === 'blocks' && (
                                        <BlocksTab
                                            blocks={p.blocks} hidden={p.hidden} meta={SCREEN_BLOCK_META}
                                            onChange={(patch) => setScreenPrefs(screenKey, patch)}
                                            hint="Arraste para mudar a ordem dos blocos acima da lista."
                                        />
                                    )}
                                    {listTab === 'kpis' && (
                                        <KpisTab kpis={p.kpis} kpiStyle={p.kpiStyle} onChange={(patch) => setScreenPrefs(screenKey, patch)} />
                                    )}
                                    {listTab === 'chart' && <ScreenChartTab screenKey={screenKey} />}
                                    {listTab === 'list' && <ListTabPanel screenKey={screenKey} />}
                                </>
                            ) : dataReg ? (
                                <>
                                    <Tabs opts={dataTabs} value={dataTab}
                                        onChange={(k) => setDrawer(k === 'list' ? (p.display === 'cards' ? 'cards' : 'cols') : k)} />
                                    {dataTab === 'blocks' && (
                                        <BlocksTab
                                            blocks={p.blocks.filter((b) => dataReg.blocks.includes(b))}
                                            hidden={p.hidden} meta={SCREEN_BLOCK_META}
                                            onChange={(patch) => setScreenPrefs(screenKey, patch)}
                                            hint="Arraste para mudar a ordem dos blocos acima da lista. Esta tela não tem funil de entrega: ela conta cliques e pessoas, não mensagens enviadas."
                                        />
                                    )}
                                    {dataTab === 'kpis' && (
                                        <DataKpisTab kpiStyle={p.kpiStyle} onChange={(patch) => setScreenPrefs(screenKey, patch)} />
                                    )}
                                    {dataTab === 'chart' && <ScreenChartTab screenKey={screenKey} />}
                                    {dataTab === 'list' && (
                                        <>
                                            <DisplayRow screenKey={screenKey} />
                                            <PlainTab screenKey={screenKey} />
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <DisplayRow screenKey={screenKey} />
                                    <PlainTab screenKey={screenKey} />
                                </>
                            )}
                        </div>
                        <div style={{ padding: '14px 22px', borderTop: `1px solid ${v('border')}`, display: 'flex', gap: 10, flexShrink: 0 }}>
                            <button type="button" onClick={() => (isOverview ? resetOverview() : resetScreen(screenKey))}
                                style={{ height: 36, padding: '0 14px', borderRadius: 10, border: `1px solid ${v('border-2')}`, background: 'transparent', color: v('text-2'), font: 'inherit', fontSize: 13, cursor: 'pointer' }}>
                                Restaurar padrão
                            </button>
                            <button type="button" onClick={close}
                                style={{ marginLeft: 'auto', height: 36, padding: '0 18px', borderRadius: 10, border: 0, background: v('text'), color: v('bg'), font: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                Concluir
                            </button>
                        </div>
                    </div>

                    {wide && <PreviewPane screenKey={screenKey} tab={tab} blockTab={blockTab} />}
                </div>
            </div>
        </div>
    );
}

/* ----------------------------------------------------------------- pré-visualização */

/** O painel da direita: a tela de verdade, com o dado do período, desenhada do jeito escolhido. */
function PreviewPane({ screenKey, tab, blockTab }: { screenKey: ScreenKey; tab: Tab; blockTab: ScreenBlock | 'all' | null }) {
    const { preview, blocks } = useEmail();
    const reg = preview.current;
    return (
        <div style={{
            flex: 1, minWidth: 0, overflow: 'auto', padding: '22px 24px 28px',
            display: 'flex', flexDirection: 'column', gap: 14,
            backgroundColor: v('bg'),
            backgroundImage: `radial-gradient(${v('track')} 1px, transparent 1px)`,
            backgroundSize: '18px 18px',
        }}>
            {blockTab && blocks.current
                ? (blocks.current.kind === 'data'
                    ? <DataBlocksPreview screenKey={screenKey} reg={blocks.current} only={blockTab} />
                    : <BlocksPreview screenKey={screenKey} reg={blocks.current} only={blockTab} />)
                : (
                    <>
                        {reg?.kind === 'list' && <ListPreview screenKey={screenKey} reg={reg} />}
                        {reg?.kind === 'data' && <DataPreview screenKey={screenKey} reg={reg} />}
                        {reg?.kind === 'overview' && <OverviewPreview reg={reg} tab={tab} />}
                        {/*
                          * Sem lista registrada, as duas causas são diferentes e não se podem contar na mesma
                          * frase: a tela não ter linha no período, ou esta VISÃO desenhar a lista num formato
                          * próprio (as peças do teste, a receita do domínio). O registro dos blocos separa as
                          * duas: ele existe sempre que a tela desenhou.
                          */}
                        {!reg && (
                            <>
                                <PreviewTitle sub={blocks.current ? 'esta visão não se reproduz aqui' : 'nada para desenhar'} />
                                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                                    {blocks.current
                                        ? 'Esta visão desenha a lista num formato próprio, e a pré-visualização não a reproduz. Os ajustes ficam salvos e valem nas visões desta tela que usam tabela ou cards.'
                                        : 'Esta tela ainda não tem o que mostrar no período — a pré-visualização aparece quando a lista tiver linha.'}
                                </p>
                            </>
                        )}
                    </>
                )}
        </div>
    );
}

function PreviewTitle({ sub }: { sub: string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Pré-visualização</span>
            <span style={{ fontSize: 12.5, color: v('faint') }}>{sub}</span>
        </div>
    );
}

/**
 * A lista, nos dois desenhos. As colunas de número se remontam aqui, com a preferência de agora:
 * tirar uma coluna tem de aparecer no mesmo instante, e não uma renderização depois.
 */
function ListPreview({ screenKey, reg }: { screenKey: ScreenKey; reg: Extract<ReturnType<typeof useEmail>['preview']['current'], { kind: 'list' }> }) {
    const { prefs, setScreenPrefs, fmt } = useEmail();
    const p = prefs.screens[screenKey];
    const cards = p.display === 'cards';
    const cols = useMemo(
        () => [...reg.idCols, ...metricCols<unknown>(p.cols, fmt, reg.card.pick, { derived: reg.card.derived })],
        [reg, p.cols, fmt],
    );
    const rows = useMemo(() => sortRows([...reg.rows], cols, p.sort ?? null), [reg, cols, p.sort]);
    const take = cards ? PREVIEW_CARDS : PREVIEW_ROWS;
    const shown = rows.slice(0, take);

    if (!rows.length) {
        return (
            <>
                <PreviewTitle sub="sem linha no período" />
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    A lista desta tela não tem linha para desenhar. Os ajustes ficam salvos do mesmo jeito.
                </p>
            </>
        );
    }

    return (
        <>
            <PreviewTitle sub={`${shown.length === rows.length ? `${rows.length}` : `as ${shown.length} primeiras de ${rows.length}`} ${rows.length === 1 ? 'linha' : 'linhas'}, com o dado real do período`} />
            {cards ? (
                <MetricCards rows={shown} card={reg.card} p={p} fmt={fmt} rowKey={reg.rowKey} flush />
            ) : (
                <div style={{ background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 16, overflow: 'hidden' }}>
                    <MetricTable
                        rows={shown}
                        cols={cols}
                        rowKey={reg.rowKey}
                        sort={p.sort}
                        onSort={(sort) => setScreenPrefs(screenKey, { sort })}
                        grouped={p.grouped}
                        noFilters
                    />
                </div>
            )}
            <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                {cards
                    ? 'A grade é a da tela: quantos cards cabem lado a lado depende da largura e do tamanho escolhido.'
                    : 'O cabeçalho ordena aqui como ordena na tela — e a ordem escolhida fica salva.'}
            </p>
        </>
    );
}

/** A lista de colunas próprias (Cliques, fontes de Leads): nada a remontar, só os dois desenhos. */
function DataPreview({ screenKey, reg }: { screenKey: ScreenKey; reg: Extract<ReturnType<typeof useEmail>['preview']['current'], { kind: 'data' }> }) {
    const { prefs, setScreenPrefs } = useEmail();
    const p = prefs.screens[screenKey];
    const cards = p.display === 'cards';
    const rows = useMemo(() => sortRows([...reg.rows], reg.cols, p.sort ?? null), [reg, p.sort]);
    const shown = rows.slice(0, cards ? PREVIEW_CARDS : PREVIEW_ROWS);

    if (!rows.length) {
        return (
            <>
                <PreviewTitle sub="sem linha no período" />
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    A lista desta tela não tem linha para desenhar. Os ajustes ficam salvos do mesmo jeito.
                </p>
            </>
        );
    }

    return (
        <>
            <PreviewTitle sub={`${shown.length === rows.length ? `${rows.length}` : `as ${shown.length} primeiras de ${rows.length}`} ${rows.length === 1 ? 'linha' : 'linhas'}, com o dado real do período`} />
            {cards ? (
                <DataCards rows={shown} cols={reg.cols} rowKey={reg.rowKey} size={p.cardSize} flush />
            ) : (
                <div style={{ background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 16, overflow: 'hidden' }}>
                    <MetricTable rows={shown} cols={reg.cols} rowKey={reg.rowKey} sort={p.sort}
                        onSort={(sort) => setScreenPrefs(screenKey, { sort })} noFilters />
                </div>
            )}
        </>
    );
}

/** A Geral: a aba aberta diz qual bloco desenhar; em "Blocos", a tela inteira, na ordem escolhida. */
function OverviewPreview({ reg, tab }: { reg: Extract<ReturnType<typeof useEmail>['preview']['current'], { kind: 'overview' }>; tab: Tab }) {
    const { prefs } = useEmail();
    const o = prefs.overview;
    const hidden = new Set(o.hidden);
    const only: OverviewBlock | null = tab === 'kpis' ? 'kpis' : tab === 'chart' ? 'chart' : null;
    const list = only ? [only] : o.blocks.filter((b) => !hidden.has(b) && b !== 'note');
    return (
        <>
            <PreviewTitle sub={only ? 'o bloco como ele vai ficar na tela' : 'a tela inteira, na ordem escolhida'} />
            {only && hidden.has(only) && (
                <p style={{ margin: 0, fontSize: 11.5, color: c('amber') }}>
                    Este bloco está desligado na aba Blocos — ele não aparece na tela.
                </p>
            )}
            {list.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint') }}>Todos os blocos estão desligados.</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
                    {list.map((b) => <Fragment key={b}>{reg.block(b, o)}</Fragment>)}
                </div>
            )}
        </>
    );
}

/* ----------------------------------------------------------------- blocos */

/** Os blocos de uma tela — os da Geral e os de uma lista têm a mesma anatomia. */
function BlocksTab<B extends string>({ blocks, hidden: hiddenList, meta: metas, onChange, hint }: {
    blocks: B[];
    hidden: B[];
    meta: Record<B, { l: string; d: string; locked?: boolean }>;
    onChange: (patch: { blocks?: B[]; hidden?: B[] }) => void;
    hint: string;
}) {
    const hidden = new Set(hiddenList);
    const move = (from: number, to: number) => {
        if (to < 0 || to >= blocks.length) return;
        const next = [...blocks];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange({ blocks: next });
    };
    return (
        <>
            <p style={{ margin: '0 0 2px', fontSize: 12.5, color: v('muted') }}>{hint}</p>
            <Reorder
                items={blocks}
                onMove={move}
                render={(block: B) => {
                    const meta = metas[block];
                    return (
                        <>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {meta.l}
                                    {meta.locked && <Lock size={12} color={v('faint')} />}
                                </div>
                                <div style={{ fontSize: 11.5, color: v('muted') }}>{meta.d}</div>
                            </div>
                            {meta.locked ? (
                                <span style={{ fontSize: 11.5, color: v('faint') }}>sempre visível</span>
                            ) : (
                                <Toggle
                                    on={!hidden.has(block)}
                                    label={`Mostrar ${meta.l}`}
                                    onChange={() => onChange({
                                        hidden: hidden.has(block) ? hiddenList.filter((b) => b !== block) : [...hiddenList, block],
                                    })}
                                />
                            )}
                        </>
                    );
                }}
            />
        </>
    );
}

/* ----------------------------------------------------------------- indicadores */

/** Os indicadores de uma tela: na Geral somam o período, numa lista somam as linhas dela. */
/** Os quatro desenhos da faixa de indicadores — o que toda tela com indicador tem. */
function KpiStyleRow({ kpiStyle, onChange }: {
    kpiStyle: KpiStyle;
    onChange: (patch: { kpiStyle?: KpiStyle }) => void;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12.5, color: v('muted') }}>Estilo</span>
            <Segmented
                opts={KPI_STYLES.map((st) => ({ k: st.k, l: st.l }))}
                value={kpiStyle}
                onChange={(style: KpiStyle) => onChange({ kpiStyle: style })}
            />
        </div>
    );
}

/**
 * Os indicadores de uma tela sem as 32 métricas: aqui não há o que escolher — são três ou quatro
 * números, e todos cabem. O que se ajusta é o desenho.
 */
function DataKpisTab({ kpiStyle, onChange }: {
    kpiStyle: KpiStyle;
    onChange: (patch: { kpiStyle?: KpiStyle }) => void;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: v('muted'), lineHeight: 1.5 }}>
                Os indicadores desta tela são os dela — cliques, links, contatos — e são poucos: todos aparecem, não
                há o que escolher. O que você ajusta aqui é o desenho da faixa.
            </p>
            <KpiStyleRow kpiStyle={kpiStyle} onChange={onChange} />
            <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                Um indicador em “—” não é zero: é um número que não existe. Contato único, por exemplo, não soma
                entre linhas — a mesma pessoa pode estar em dois links.
            </p>
        </div>
    );
}

function KpisTab({ kpis: chosen, kpiStyle, onChange }: {
    kpis: string[];
    kpiStyle: KpiStyle;
    onChange: (patch: { kpis?: string[]; kpiStyle?: KpiStyle }) => void;
}) {
    const toggle = (k: string) => onChange({ kpis: chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k] });
    const move = (from: number, to: number) => {
        if (to < 0 || to >= chosen.length) return;
        const next = [...chosen];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange({ kpis: next });
    };
    const available = useMemo(() => KPI_CHOICES.filter((k) => !chosen.includes(k)), [chosen]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <KpiStyleRow kpiStyle={kpiStyle} onChange={onChange} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 12.5, color: v('muted') }}>Escolhidos · {chosen.length}</span>
                <Reorder items={chosen} onMove={move} render={(k: string) => (
                    <>
                        <span className="rv-mono" style={{ fontSize: 11, color: v('faint'), width: 18 }}>
                            {String(chosen.indexOf(k) + 1).padStart(2, '0')}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5 }}>{M[k].l}{M[k].den && <span style={{ color: v('faint') }}> {M[k].den}</span>}</div>
                            <div style={{ fontSize: 11.5, color: v('muted') }}>{BLOCK_LABEL[M[k].block]}</div>
                        </div>
                        <Toggle on onChange={() => toggle(k)} label={`Tirar ${M[k].l}`} />
                    </>
                )} />
            </div>
            {available.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12.5, color: v('muted') }}>Disponíveis</span>
                    {available.map((k) => (
                        <button key={k} type="button" onClick={() => toggle(k)} className="rv-h-hover" title={M[k].d}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, height: 36, padding: '0 10px', border: 0, borderRadius: 9, background: 'transparent', color: v('text-3'), font: 'inherit', fontSize: 13, textAlign: 'left', cursor: 'pointer' }}>
                            <Check on={false} />
                            <span style={{ flex: 1 }}>{M[k].l}{M[k].den && <span style={{ color: v('faint') }}> {M[k].den}</span>}</span>
                            <span style={{ fontSize: 11.5, color: v('faint') }}>{BLOCK_LABEL[M[k].block]}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ----------------------------------------------------------------- gráfico */

function ChartTab() {
    const { prefs, setOverviewPrefs } = useEmail();
    const current = prefs.overview.chart;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: v('muted') }}>
                O modelo do gráfico principal. As três taxas juntas num eixo só não se leem — para isso existe o
                bloco “Taxas, dia a dia”, com uma escala para cada. Em “Métrica × métrica” você põe uma delas num
                eixo e o que quiser no outro.
            </p>
            {(Object.keys(CHART_MODELS) as ChartModel[]).map((k) => {
                const on = k === current;
                return (
                    <button key={k} type="button" onClick={() => setOverviewPrefs({ chart: k })} className={on ? '' : 'rv-h-hover'}
                        style={{
                            display: 'flex', flexDirection: 'column', gap: 3, padding: '10px 12px', borderRadius: 10,
                            border: `1px solid ${on ? v('text') : v('border-2')}`, background: on ? v('border') : 'transparent',
                            color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                        }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{CHART_MODELS[k][0]}</span>
                        <span style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.4 }}>{CHART_MODELS[k][1]}</span>
                    </button>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- a lista */

/** "Exibir como": a mesma linha nas duas formas de tela de lista. */
function DisplayRow({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs, setDrawer } = useEmail();
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5 }}>Exibir como</div>
                <div style={{ fontSize: 11.5, color: v('muted') }}>Os mesmos dados — muda o desenho da linha.</div>
            </div>
            <Segmented
                opts={DISPLAYS}
                value={prefs.screens[screenKey].display}
                onChange={(display: Display) => { setScreenPrefs(screenKey, { display }); setDrawer(display === 'cards' ? 'cards' : 'cols'); }}
            />
        </div>
    );
}

function ListTabPanel({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs } = useEmail();
    return (
        <>
            <DisplayRow screenKey={screenKey} />
            {prefs.screens[screenKey].display === 'cards'
                ? <CardsTab screenKey={screenKey} />
                : <ColsTab screenKey={screenKey} />}
        </>
    );
}

/**
 * O gráfico de uma tela de lista. O catálogo não é fixo: os três de todas as telas mais o que esta
 * trouxer — e quem sabe qual é ela é a tela no ar, que se registrou.
 */
function ScreenChartTab({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs, blocks } = useEmail();
    const models = regModels(blocks.current);
    // o que a tela desenha agora, não só o que está salvo: nesta visão a escolha pode não existir
    const chosen = chartModel(models, prefs.screens[screenKey].chart);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: '0 0 2px', fontSize: 12.5, color: v('muted'), lineHeight: 1.5 }}>
                Estas telas não têm série por dia — a API manda as linhas somadas do período, não um ponto por data.
                Por isso os modelos comparam linhas entre si, em vez de desenhar o tempo. Qual número comparar você
                escolhe no próprio gráfico, nos botões acima das barras.
            </p>
            {models.map((m) => {
                const on = m.k === chosen;
                return (
                    <button key={m.k} type="button" onClick={() => setScreenPrefs(screenKey, { chart: m.k })}
                        className={on ? '' : 'rv-h-hover'}
                        style={{
                            display: 'flex', flexDirection: 'column', gap: 2, padding: '11px 13px', borderRadius: 12,
                            border: `1px solid ${on ? v('text') : v('border-2')}`, background: on ? v('border') : 'transparent',
                            color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                        }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{m.l}</span>
                        <span style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.4 }}>{m.d}</span>
                    </button>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- forma do card */

function CardSizeRow({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs } = useEmail();
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5 }}>Tamanho do card</div>
                <div style={{ fontSize: 11.5, color: v('muted') }}>Quantos cabem lado a lado — a grade se ajusta à largura.</div>
            </div>
            <Segmented
                opts={CARD_SIZES.map((s) => ({ k: s.k, l: s.l }))}
                value={prefs.screens[screenKey].cardSize}
                onChange={(cardSize: CardSize) => setScreenPrefs(screenKey, { cardSize })}
            />
        </div>
    );
}

/**
 * Telas cuja lista NÃO é feita das métricas de envio: Cliques conta links e Leads conta pessoas. Ali as
 * colunas são próprias da tela e não há o que escolher — o que se personaliza é a forma.
 */
function PlainTab({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs } = useEmail();
    const p = prefs.screens[screenKey];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: v('muted'), lineHeight: 1.5 }}>
                As colunas desta lista são dela — cliques, contatos, leads — e não as {METRICS.length} métricas de
                envio das outras telas. Por isso não há coluna para escolher aqui: o que muda é o desenho.
            </p>
            {p.display === 'cards'
                ? <CardSizeRow screenKey={screenKey} />
                : (
                    <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                        Em cards você ainda escolhe o tamanho. Na tabela, as colunas são as que a tela traz.
                    </p>
                )}
        </div>
    );
}

/* ----------------------------------------------------------------- tabela */

function ColsTab({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs } = useEmail();
    const p = prefs.screens[screenKey];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: v('muted') }}>
                São {METRICS.length} números por linha — não cabem todos. Escolha os que você olha e arraste para
                mudar a ordem na tabela; a escolha fica salva.
            </p>
            <GroupedSwitch screenKey={screenKey} what="as colunas" />
            <MetricPicker
                title="Na tabela"
                chosen={p.cols}
                onChange={(cols) => setScreenPrefs(screenKey, { cols })}
                empty="Nenhuma coluna de número — a tabela fica só com a identificação da linha."
                hint={p.grouped ? 'Com a separação ligada a tabela junta cada bloco; esta ordem vale dentro do bloco.' : undefined}
            />
        </div>
    );
}

/* ----------------------------------------------------------------- cards */

function CardsTab({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs, preview } = useEmail();
    const p = prefs.screens[screenKey];
    const reg = preview.current;
    const spec = reg?.kind === 'list' ? reg.card : undefined;
    // o bloco próprio só existe onde a tela tem um; sem ele, nem aparece na lista de blocos
    const blocks = CARD_BLOCKS.filter((b) => b !== 'extra' || spec?.extra);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <CardSizeRow screenKey={screenKey} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 12.5, color: v('muted') }}>Blocos do card</span>
                {blocks.map((b) => {
                    const meta = CARD_BLOCK_META[b];
                    const label = b === 'extra' ? spec?.extraLabel ?? meta.l : meta.l;
                    const on = p.cardBlocks.includes(b);
                    return (
                        <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${v('border')}`, borderRadius: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {label}
                                    {meta.locked && <Lock size={12} color={v('faint')} />}
                                </div>
                                <div style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.45 }}>{meta.d}</div>
                            </div>
                            {meta.locked ? (
                                <span style={{ fontSize: 11.5, color: v('faint'), whiteSpace: 'nowrap' }}>sempre visível</span>
                            ) : (
                                <Toggle on={on} label={`Mostrar ${label}`}
                                    onChange={() => setScreenPrefs(screenKey, {
                                        cardBlocks: on ? p.cardBlocks.filter((x) => x !== b) : [...p.cardBlocks, b as CardBlock],
                                    })} />
                            )}
                        </div>
                    );
                })}
            </div>

            <GroupedSwitch screenKey={screenKey} what="as métricas do card" />
            <CardSort screenKey={screenKey} />
            <MetricPicker
                title="No card"
                chosen={p.cardMetrics}
                onChange={(cardMetrics) => setScreenPrefs(screenKey, { cardMetrics })}
                empty="Nenhum número no card — fica só a identificação e os blocos ligados acima."
                hint={p.grouped ? 'Com a separação ligada, cada bloco vira uma faixa, e o primeiro número da faixa é o destaque.' : undefined}
            />
        </div>
    );
}

/**
 * A ordenação dos cards. Na tabela quem ordena é o cabeçalho; o card não tem cabeçalho para
 * clicar, e sem isto a lista em cards ficaria presa na ordem que a API mandou.
 */
function CardSort({ screenKey }: { screenKey: ScreenKey }) {
    const { prefs, setScreenPrefs } = useEmail();
    const p = prefs.screens[screenKey];
    const keys = p.cardMetrics.filter((k) => M[k]);
    const set = (sort: SortPref | null) => setScreenPrefs(screenKey, { sort });
    const cycle = (k: string) => {
        if (p.sort?.key !== k) return set({ key: k, dir: 'desc' });
        if (p.sort.dir === 'desc') return set({ key: k, dir: 'asc' });
        return set(null); // o terceiro clique devolve a ordem da API, como na tabela
    };
    const row = (on: boolean, label: string, right: string, onClick: () => void) => (
        <button key={label} type="button" onClick={onClick} className={on ? '' : 'rv-h-hover'}
            style={{
                display: 'flex', alignItems: 'center', gap: 10, height: 36, padding: '0 10px', borderRadius: 9,
                border: 0, background: 'transparent', color: on ? v('text') : v('text-3'), font: 'inherit', fontSize: 13,
                textAlign: 'left', cursor: 'pointer',
            }}>
            <Check on={on} />
            <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
            <span style={{ fontSize: 11.5, color: v('faint'), whiteSpace: 'nowrap' }}>{right}</span>
        </button>
    );
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12.5, color: v('muted'), marginBottom: 4 }}>Ordenar por</span>
            {row(p.sort == null, 'Ordem da API', 'como veio', () => set(null))}
            {keys.map((k) => row(
                p.sort?.key === k,
                M[k].l,
                p.sort?.key === k ? (p.sort.dir === 'desc' ? 'maior → menor' : 'menor → maior') : '',
                () => cycle(k),
            ))}
            {keys.length === 0 && (
                <p style={{ margin: 0, fontSize: 11.5, color: v('faint') }}>Escolha uma métrica abaixo para poder ordenar por ela.</p>
            )}
        </div>
    );
}

/* ----------------------------------------------------------------- peças compartilhadas */

/** "Separar por origem" é uma chave só: vale para as colunas da tabela e para as faixas do card. */
function GroupedSwitch({ screenKey, what }: { screenKey: ScreenKey; what: string }) {
    const { prefs, setScreenPrefs } = useEmail();
    const p = prefs.screens[screenKey];
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${v('border')}`, borderRadius: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5 }}>Separar por origem</div>
                <div style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.45 }}>
                    Junta {what} por bloco — Receita, Envios e Taxas — com faixa e cor em cada um. Vale para a tabela e para os cards.
                </div>
            </div>
            <Toggle on={!!p.grouped} label="Separar por origem" onChange={() => setScreenPrefs(screenKey, { grouped: !p.grouped })} />
        </div>
    );
}

/** O escolhe-e-ordena das métricas: serve às colunas da tabela e às métricas do card. */
function MetricPicker({ title, chosen, onChange, empty, hint }: {
    title: string;
    chosen: string[];
    onChange: (next: string[]) => void;
    empty: string;
    hint?: string;
}) {
    const toggle = (k: string) => onChange(chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k]);
    const move = (from: number, to: number) => {
        if (to < 0 || to >= chosen.length) return;
        const next = [...chosen];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    const activeSet = COLUMN_SETS.find((s) => s.cols.length === chosen.length && s.cols.every((k) => chosen.includes(k)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COLUMN_SETS.map((s) => {
                    const on = activeSet?.k === s.k;
                    return (
                        <button key={s.k} type="button" onClick={() => onChange([...s.cols])}
                            style={{
                                height: 28, padding: '0 11px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                                border: `1px solid ${on ? v('text') : v('border')}`,
                                background: on ? v('text') : 'transparent', color: on ? v('bg') : v('text-3'),
                            }}>
                            {s.l}
                        </button>
                    );
                })}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 12.5, color: v('muted') }}>{title} · {chosen.length}</span>
                {hint && <span style={{ fontSize: 11.5, color: v('faint'), lineHeight: 1.45 }}>{hint}</span>}
                {chosen.length === 0
                    ? <p style={{ margin: 0, fontSize: 12.5, color: v('faint') }}>{empty}</p>
                    : (
                        <Reorder items={chosen} onMove={move} render={(k: string) => (
                            <>
                                <span className="rv-mono" style={{ fontSize: 11, color: v('faint'), width: 18 }}>
                                    {String(chosen.indexOf(k) + 1).padStart(2, '0')}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13.5 }}>{M[k].l}{M[k].den && <span style={{ color: v('faint') }}> {M[k].den}</span>}</div>
                                    <div style={{ fontSize: 11.5, color: c(BLOCK_HUE[M[k].block]) }}>{BLOCK_LABEL[M[k].block]}</div>
                                </div>
                                <Toggle on onChange={() => toggle(k)} label={`Tirar ${M[k].l}`} />
                            </>
                        )} />
                    )}
            </div>

            {BLOCKS.map((block) => {
                const available = METRICS.filter((m) => m.block === block && !chosen.includes(m.k));
                if (!available.length) return null;
                return (
                    <div key={block} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: c(BLOCK_HUE[block]), padding: '4px 2px' }}>
                            {BLOCK_LABEL[block]}
                        </span>
                        {available.map((m) => (
                            <button key={m.k} type="button" onClick={() => toggle(m.k)} className="rv-h-hover" title={m.d}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, height: 36, padding: '0 10px', border: 0, borderRadius: 9, background: 'transparent', color: v('text-3'), font: 'inherit', fontSize: 13, textAlign: 'left', cursor: 'pointer' }}>
                                <Check on={false} />
                                <span style={{ flex: 1 }}>{m.l}{m.den && <span style={{ color: v('faint') }}> {m.den}</span>}</span>
                            </button>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- reordenar */

/** Arrastar com o mouse, subir/descer pelo teclado. */
function Reorder<T extends string>({ items, onMove, render }: {
    items: T[];
    onMove: (from: number, to: number) => void;
    render: (item: T) => ReactNode;
}) {
    const [dragging, setDragging] = useState<number | null>(null);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((item, i) => (
                <div
                    key={item}
                    draggable
                    onDragStart={() => setDragging(i)}
                    onDragEnd={() => setDragging(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        if (dragging != null && dragging !== i) onMove(dragging, i);
                        setDragging(null);
                    }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        border: `1px solid ${v('border')}`, borderRadius: 12, background: v('surface'),
                        opacity: dragging === i ? 0.5 : 1,
                    }}>
                    <GripVertical size={15} color={v('faint')} style={{ cursor: 'grab', flexShrink: 0 }} aria-hidden />
                    {render(item)}
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button type="button" onClick={() => onMove(i, i - 1)} disabled={i === 0} aria-label="Subir" style={arrowBtn(i === 0)}>
                            <ChevronUp size={13} />
                        </button>
                        <button type="button" onClick={() => onMove(i, i + 1)} disabled={i === items.length - 1} aria-label="Descer" style={arrowBtn(i === items.length - 1)}>
                            <ChevronDown size={13} />
                        </button>
                    </span>
                </div>
            ))}
        </div>
    );
}

const arrowBtn = (disabled: boolean) => ({
    width: 22, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 0, borderRadius: 5, background: 'transparent', color: v('muted'),
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1, padding: 0,
}) as const;
